import React, { useState, useEffect } from 'react';
import { getAccessToken } from '../services/auth';

// The shipping rulebook. One source for call center training, Ask, Jimmy and the Order
// Updates reviewer. Every change is a read-back ("Changing the rule to... Before it
// was...") and rewrites the knowledge base doc. Backend: netlify/functions/lib/shipping-rules.js.

const SECTIONS = [
  ['methods', 'The five shipping methods'],
  ['product', '1. Product rules'],
  ['destination', '2. Destination rules'],
  ['calendar', '3. Calendar and cutoff'],
  ['prices', '4. Prices'],
  ['override', '5. CS phone orders (the override box)'],
  ['bugs', 'Known bugs in the checkout code'],
  ['sources', 'Where each rule lives'],
];
const STATUS = {
  confirmed: { label: 'Confirmed', bg: '#e3f4ea', fg: '#1f6b43' },
  draft: { label: 'From the code', bg: '#eef2f6', fg: '#3f5566' },
  open: { label: 'Open question', bg: '#fff3c4', fg: '#7a5b00' },
  info: { label: 'Info', bg: '#f4f4f4', fg: '#666' },
};

function fnBase() {
  return window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
}
async function call(method, body) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${fnBase()}/shipping-rules`, { method, headers, body: method === 'GET' ? undefined : JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.ok === false) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '');

function Chip({ status }) {
  const s = STATUS[status] || STATUS.info;
  return <span style={{ background: s.bg, color: s.fg, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

// Edit, then read it back, then confirm. Nothing is saved until Confirm.
function Editor({ rule, onDone, onCancel }) {
  const [text, setText] = useState(rule.rule_text);
  const [status, setStatus] = useState(rule.status === 'open' ? 'confirmed' : rule.status);
  const [question, setQuestion] = useState(rule.question || '');
  const [note, setNote] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [step, setStep] = useState('edit');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const changed = text.trim() !== rule.rule_text || status !== rule.status;

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const out = await call('POST', { action: 'change', id: rule.id, ruleText: text.trim(), status, question: status === 'open' ? question : null, note, needsCode });
      onDone(out);
    } catch (e) { setError(e.message); setBusy(false); }
  };

  const box = { border: '1px solid var(--gray-200)', borderRadius: 8, padding: 12, background: '#fafbfc', marginTop: 8 };
  if (step === 'readback') {
    return (
      <div style={box}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Read it back before you confirm</div>
        <p style={{ margin: '8px 0 4px' }}><b>Changing the rule to:</b> {text.trim()} <Chip status={status} /></p>
        <p style={{ margin: '4px 0', color: 'var(--gray-600)' }}><b>Before it was:</b> {rule.rule_text} <Chip status={rule.status} /></p>
        {note && <p style={{ margin: '4px 0', color: 'var(--gray-600)' }}><b>Note:</b> {note}</p>}
        {needsCode && <p style={{ margin: '4px 0', color: '#a14a12' }}>Marked as needing a checkout code change. Greg gets an email to hand to Claude.</p>}
        <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--gray-500)' }}>Is that exactly right? If speech-to-text changed a word, go back and fix it.</p>
        {error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 6 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving...' : 'Confirm, it\'s right'}</button>
          <button className="btn btn-outline" disabled={busy} onClick={() => setStep('edit')}>Go back</button>
        </div>
      </div>
    );
  }
  return (
    <div style={box}>
      {rule.status === 'open' && rule.question && <p style={{ margin: '0 0 8px' }}><b>Question:</b> {rule.question}</p>}
      <label style={{ fontSize: 12, fontWeight: 600 }}>The rule, in plain words</label>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} style={{ width: '100%', boxSizing: 'border-box', marginTop: 4, padding: 8, fontSize: 13, fontFamily: 'inherit', border: '1px solid var(--gray-300)', borderRadius: 6 }} />
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
        <label style={{ fontSize: 12 }}>Status{' '}
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: 4 }}>
            <option value="confirmed">Confirmed</option>
            <option value="draft">From the code (not yet confirmed)</option>
            <option value="open">Open question</option>
            <option value="info">Info</option>
          </select>
        </label>
        <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={needsCode} onChange={(e) => setNeedsCode(e.target.checked)} /> The checkout has to change to match
        </label>
      </div>
      {status === 'open' && (
        <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="The open question" style={{ width: '100%', boxSizing: 'border-box', marginTop: 8, padding: 6, fontSize: 13, border: '1px solid var(--gray-300)', borderRadius: 6 }} />
      )}
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (optional): who decided, or what it was based on" style={{ width: '100%', boxSizing: 'border-box', marginTop: 8, padding: 6, fontSize: 13, border: '1px solid var(--gray-300)', borderRadius: 6 }} />
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn btn-navy" disabled={!changed && !note} onClick={() => setStep('readback')}>Review the change</button>
        <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function RuleCard({ rule, canEdit, editing, setEditing, onSaved }) {
  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--gray-100)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <b style={{ color: 'var(--navy)', fontSize: 14 }}>{rule.title}</b>
        <Chip status={rule.status} />
        {canEdit && editing !== rule.id && rule.section !== 'methods' && (
          <button className="btn btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => setEditing(rule.id)}>{rule.status === 'open' ? 'Answer' : 'Change'}</button>
        )}
      </div>
      <div style={{ fontSize: 13.5, lineHeight: 1.5, marginTop: 3 }}>{rule.rule_text}</div>
      {rule.status === 'open' && rule.question && <div style={{ fontSize: 13, color: '#7a5b00', marginTop: 3 }}><b>Question:</b> {rule.question}</div>}
      {(rule.source || rule.updated_by) && (
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 3 }}>
          {rule.source ? `Source: ${rule.source}` : ''}{rule.source && rule.updated_by ? ' · ' : ''}{rule.updated_by ? `Last set by ${rule.updated_by}, ${fmt(rule.updated_at)}` : ''}
        </div>
      )}
      {editing === rule.id && <Editor rule={rule} onCancel={() => setEditing(null)} onDone={onSaved} />}
    </div>
  );
}

export default function ShippingRules() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState(null);
  const [token, setToken] = useState('');
  const [tokenMsg, setTokenMsg] = useState(null);

  const load = () => call('GET').then(setData).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const onSaved = (out) => {
    setEditing(null);
    const bits = ['Saved.'];
    bits.push(out.kb ? 'The knowledge base doc is updated.' : `The knowledge base doc did not update (${out.kbError || 'unknown error'}); the rule itself is saved.`);
    if (out.change && out.change.needs_code) bits.push(out.emailed ? 'Greg has the email for the code change.' : 'The email to Greg did not send; tell him.');
    setNotice(bits.join(' '));
    load();
  };
  const saveToken = async () => {
    setTokenMsg(null);
    try { await call('POST', { action: 'github_token', token }); setToken(''); setTokenMsg('Saved. The tripwire runs Mondays at 8 AM.'); load(); } catch (e) { setTokenMsg(e.message); }
  };

  if (error) return <div style={{ padding: 16, color: 'var(--red)' }}>Error: {error}</div>;
  if (!data) return <div style={{ padding: 16, color: 'var(--gray-500)' }}>Loading...</div>;
  const rules = data.rules || [];
  const open = rules.filter((r) => r.status === 'open');
  const cardProps = { canEdit: data.canEdit, editing, setEditing, onSaved };

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: '0 0 4px' }}>Shipping Rules</h2>
      <p style={{ fontSize: 13, color: 'var(--gray-600)', margin: '0 0 12px', lineHeight: 1.5 }}>
        The one place for shipping rules: what methods, delivery days and charges an order can get. Call center training, Ask, Jimmy and the Order Updates reviewer all follow this page.
        Every change is read back before it saves, and updates the knowledge base doc by itself.
      </p>
      {notice && <div className="card" style={{ padding: 12, marginBottom: 12, fontSize: 13, background: '#e3f4ea', color: '#1f6b43' }}>{notice}</div>}

      {open.length > 0 && (
        <div className="card" style={{ padding: '12px 16px', marginBottom: 14, borderLeft: '4px solid #e0b000' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#7a5b00' }}>Open questions ({open.length})</div>
          <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 2 }}>Not settled yet, so don't train on these. Answer one by rewriting the rule the way it should be and marking it Confirmed.</div>
          {open.map((r) => <RuleCard key={r.id} rule={r} {...cardProps} />)}
        </div>
      )}

      {SECTIONS.map(([key, heading]) => {
        const rows = rules.filter((r) => r.section === key && r.status !== 'open');
        if (!rows.length) return null;
        return (
          <div key={key} className="card" style={{ padding: '12px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 2 }}>{heading}</div>
            {rows.map((r) => <RuleCard key={r.id} rule={r} {...cardProps} />)}
          </div>
        );
      })}

      <div className="card" style={{ padding: '12px 16px', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Change log</div>
        {(data.changes || []).length === 0 && <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 6 }}>No changes yet. First draft written from the checkout code on Oct 21, 2026.</div>}
        {(data.changes || []).map((c) => (
          <div key={c.id} style={{ fontSize: 13, padding: '8px 0', borderTop: '1px solid var(--gray-100)' }}>
            <b>{c.title}</b> <span style={{ color: 'var(--gray-500)' }}>· {c.changed_by}, {fmt(c.changed_at)}</span>
            {c.old_status !== c.new_status && <span> · <Chip status={c.old_status} /> to <Chip status={c.new_status} /></span>}
            <div style={{ color: 'var(--gray-600)', marginTop: 2 }}>Now: {c.new_text}</div>
            {c.old_text !== c.new_text && <div style={{ color: 'var(--gray-500)', marginTop: 2 }}>Before: {c.old_text}</div>}
            {c.note && <div style={{ color: 'var(--gray-500)', marginTop: 2 }}>Note: {c.note}</div>}
            {c.needs_code && <div style={{ color: '#a14a12', marginTop: 2 }}>Needs a checkout code change{c.emailed_at ? ' (Greg emailed)' : ''}</div>}
          </div>
        ))}
      </div>

      {data.isAdmin && (
        <div className="card" style={{ padding: '12px 16px', marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Tripwire</div>
          <div style={{ fontSize: 12.5, color: 'var(--gray-600)', margin: '4px 0 8px', lineHeight: 1.5 }}>
            Every Monday at 8 AM this checks whether the checkout's shipping code changed on GitHub, and emails Greg if it did.{' '}
            {data.tripwire.hasToken ? <b style={{ color: '#1f6b43' }}>Connected.</b> : <b style={{ color: '#a14a12' }}>Not connected: it needs a read-only GitHub key.</b>}
            {(data.tripwire.state || []).filter((s) => s.key === 'tripwire_checked_at').map((s) => <span key={s.key}> Last checked {fmt(s.value)}.</span>)}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder={data.tripwire.hasToken ? 'Replace the GitHub key' : 'Paste the GitHub key (github_pat_...)'} autoComplete="off"
              style={{ flex: '1 1 260px', padding: 6, fontSize: 13, border: '1px solid var(--gray-300)', borderRadius: 6 }} />
            <button className="btn btn-outline" disabled={!token} onClick={saveToken}>Save</button>
          </div>
          {tokenMsg && <div style={{ fontSize: 12, marginTop: 6, color: 'var(--gray-600)' }}>{tokenMsg}</div>}
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>The key is stored on the server and never shown again.</div>
        </div>
      )}
    </div>
  );
}
